import React, { useState, useRef } from 'react';
import { CustomField } from '@/types';

interface CustomFieldEditorProps {
  fields: CustomField[];
  onChange: (fields: CustomField[]) => void;
}

interface FieldFormData {
  label: string;
  type: CustomField['type'];
  required: boolean;
  hideOnNo: boolean;
  options: string[];
}

function OptionsEditor({ options, onChange }: { options: string[]; onChange: (opts: string[]) => void }) {
  const [newOption, setNewOption] = useState('');
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const addOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed) return;
    onChange([...options, trimmed]);
    setNewOption('');
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const reordered = [...options];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    onChange(reordered);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-600">Dropdown Options</p>
      {options.length > 0 && (
        <div className="space-y-1">
          {options.map((opt, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-200 rounded cursor-grab active:cursor-grabbing group hover:border-violet-300 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              </svg>
              <span className="text-sm text-gray-700 flex-1">{opt}</span>
              <button
                type="button"
                onClick={() => removeOption(index)}
                className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="New option..."
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
          className="input-field text-sm flex-1"
        />
        <button
          type="button"
          onClick={addOption}
          disabled={!newOption.trim()}
          className="px-3 py-1.5 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          + Add
        </button>
      </div>
    </div>
  );
}

function FieldForm({
  data,
  onSave,
  onCancel,
  saveLabel,
}: {
  data: FieldFormData;
  onSave: (data: FieldFormData) => void;
  onCancel: () => void;
  saveLabel: string;
}) {
  const [formData, setFormData] = useState<FieldFormData>(data);

  return (
    <div className="p-4 bg-violet-50 border border-violet-100 rounded-lg space-y-3 animate-slide-down">
      <input
        type="text"
        placeholder="Field label (e.g., Dietary Requirements)"
        value={formData.label}
        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
        className="input-field text-sm"
      />

      <div className="flex gap-3">
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as CustomField['type'] })}
          className="input-field text-sm flex-1"
        >
          <option value="text">Text</option>
          <option value="textarea">Text Area</option>
          <option value="dropdown">Dropdown</option>
          <option value="number">Number</option>
          <option value="checkbox">Checkbox</option>
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={formData.required}
            onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-violet-600"
          />
          Required
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={formData.hideOnNo}
          onChange={(e) => setFormData({ ...formData, hideOnNo: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300 text-violet-600"
        />
        Hide field on 'No' response
      </label>

      {formData.type === 'dropdown' && (
        <OptionsEditor
          options={formData.options}
          onChange={(opts) => setFormData({ ...formData, options: opts })}
        />
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSave(formData)}
          disabled={!formData.label}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {saveLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function CustomFieldEditor({ fields, onChange }: CustomFieldEditorProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const addField = (data: FieldFormData) => {
    const field: CustomField = {
      id: `field_${Date.now()}`,
      label: data.label,
      type: data.type,
      required: data.required,
      hideOnNo: data.hideOnNo,
      options: data.type === 'dropdown' ? data.options : undefined,
    };
    onChange([...fields, field]);
    setShowAdd(false);
  };

  const updateField = (id: string, data: FieldFormData) => {
    onChange(fields.map((f) => {
      if (f.id !== id) return f;
      return {
        ...f,
        label: data.label,
        type: data.type,
        required: data.required,
        hideOnNo: data.hideOnNo,
        options: data.type === 'dropdown' ? data.options : undefined,
      };
    }));
    setEditingId(null);
  };

  const removeField = (id: string) => {
    onChange(fields.filter((f) => f.id !== id));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newFields.length) return;
    [newFields[index], newFields[swapIndex]] = [newFields[swapIndex], newFields[index]];
    onChange(newFields);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Custom RSVP Fields</h4>
        <button
          type="button"
          onClick={() => { setShowAdd(!showAdd); setEditingId(null); }}
          className="text-sm font-medium text-violet-600 hover:text-violet-700"
        >
          {showAdd ? 'Cancel' : '+ Add Field'}
        </button>
      </div>

      {/* Existing fields */}
      {fields.length > 0 && (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id}>
              {editingId === field.id ? (
                <FieldForm
                  data={{
                    label: field.label,
                    type: field.type,
                    required: field.required,
                    hideOnNo: field.hideOnNo || false,
                    options: field.options || [],
                  }}
                  onSave={(data) => updateField(field.id, data)}
                  onCancel={() => setEditingId(null)}
                  saveLabel="Save Changes"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveField(index, 'up')}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveField(index, 'down')}
                      disabled={index === fields.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => { setEditingId(field.id); setShowAdd(false); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setEditingId(field.id); setShowAdd(false); } }}
                  >
                    <p className="text-sm font-medium text-gray-900">{field.label}</p>
                    <p className="text-xs text-gray-500">
                      {field.type} {field.required && '• required'}
                      {field.hideOnNo && ' • hidden on No'}
                      {field.options && field.options.length > 0 && ` • ${field.options.join(', ')}`}
                    </p>
                    <p className="text-xs text-violet-500 mt-0.5">Click to edit</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new field form */}
      {showAdd && (
        <FieldForm
          data={{ label: '', type: 'text', required: false, hideOnNo: false, options: [] }}
          onSave={addField}
          onCancel={() => setShowAdd(false)}
          saveLabel="Add Field"
        />
      )}

      {fields.length === 0 && !showAdd && (
        <p className="text-sm text-gray-400 italic">
          No custom fields added. Default fields (name, email, attending, guest count) are always included.
        </p>
      )}
    </div>
  );
}
